import React, { useMemo, useState } from "react";
import { Input, Button, Tag, Popconfirm, message } from "antd";
import { CaretRightOutlined, CaretDownOutlined } from "@ant-design/icons";
import aiConsoleService from "services/aiconsoleservice";

const { TextArea } = Input;

// antd v5 primary buttons render transparent under Tailwind Preflight (its :where()
// rules have zero specificity), so force the themed fill so they are visible at rest.
const PRIMARY_BTN = "!bg-[#0b1437] hover:!bg-[#1e293b] !border-[#0b1437] !text-white";

// Friendly labels for the known prompt keys; unknown keys fall back to the raw key.
const PROMPT_LABELS = {
  general: "General chat prompt",
  house_rules: "House Rules prompt",
  scope_classifier: "Scope classifier prompt",
};

const promptLabel = (key) => PROMPT_LABELS[key] || key;

const activeOf = (versions) => versions.find((v) => v.is_active) || versions[0] || null;

const PromptGroup = ({ agentKey, promptKey, versions, editable, onChanged }) => {
  const active = activeOf(versions);
  const [draft, setDraft] = useState(active?.body ?? "");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [activating, setActivating] = useState(null);
  const [expanded, setExpanded] = useState({});

  const toggle = (version) => setExpanded((e) => ({ ...e, [version]: !e[version] }));

  const save = async () => {
    if (!draft.trim()) {
      message.error("The prompt cannot be empty.");
      return;
    }
    if (!note.trim()) {
      message.error("Please enter a reason for this version.");
      return;
    }
    setSaving(true);
    try {
      await aiConsoleService.createPromptVersion(agentKey, {
        prompt_key: promptKey,
        body: draft,
        note,
      });
      message.success("Saved as a new version.");
      setNote("");
      onChanged?.();
    } catch (e) {
      message.error("Could not save the prompt.");
    } finally {
      setSaving(false);
    }
  };

  const setActive = async (version) => {
    setActivating(version);
    try {
      await aiConsoleService.setActivePrompt(agentKey, { prompt_key: promptKey, version });
      message.success(`Version ${version} is now active.`);
      onChanged?.();
    } catch (e) {
      message.error("Could not change the active version.");
    } finally {
      setActivating(null);
    }
  };

  return (
    <section className="mb-6" data-testid={`prompt-group-${promptKey}`}>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-slate-700 dark:text-gray-200">{promptLabel(promptKey)}</h3>
        {active && (
          <span className="text-xs text-gray-400">
            Active: v{active.version}
          </span>
        )}
      </div>

      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
        {promptKey === "house_rules" && (
          <p className="text-xs text-gray-400 mb-2">
            Use {"{{HOUSE_RULES_CONTENT}}"} where the compiled House Rules should be inserted. If omitted, the House Rules are appended automatically.
          </p>
        )}

        <label className="block text-sm font-medium text-slate-700 dark:text-gray-200 mb-1" htmlFor={`prompt-body-${promptKey}`}>
          Prompt text
        </label>
        <TextArea
          id={`prompt-body-${promptKey}`}
          data-testid={`prompt-body-${promptKey}`}
          className="font-mono"
          rows={10}
          readOnly={!editable}
          disabled={!editable}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
        />

        {editable && (
          <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-2">
            <Input
              data-testid={`prompt-note-${promptKey}`}
              className="sm:max-w-xs"
              placeholder="Reason for this version (required)"
              value={note}
              maxLength={255}
              onChange={(e) => setNote(e.target.value)}
            />
            <Button
              data-testid={`prompt-save-${promptKey}`}
              type="primary"
              className={PRIMARY_BTN}
              loading={saving}
              onClick={save}
            >
              Save as new version
            </Button>
          </div>
        )}
      </div>

      {versions.length > 0 && (
        <ul className="mt-3 divide-y divide-gray-100 dark:divide-gray-700 rounded-xl border border-gray-200 dark:border-gray-700">
          {versions.map((v) => (
            <li key={v.version} className="text-sm" data-testid={`prompt-version-${promptKey}-${v.version}`}>
              <div className="flex items-center justify-between px-4 py-2 gap-2">
                <button
                  type="button"
                  onClick={() => toggle(v.version)}
                  aria-expanded={!!expanded[v.version]}
                  className="flex items-center gap-2 min-w-0 text-left cursor-pointer focus:outline-none focus:ring-2 focus:ring-emerald-500 rounded"
                  data-testid={`prompt-version-toggle-${promptKey}-${v.version}`}
                >
                  {expanded[v.version] ? <CaretDownOutlined /> : <CaretRightOutlined />}
                  <span className="font-medium text-slate-700 dark:text-gray-200">v{v.version}</span>
                  {v.note && <span className="text-gray-400 truncate">{v.note}</span>}
                </button>
                <div className="shrink-0">
                  {v.is_active ? (
                    <Tag color="green">Active</Tag>
                  ) : editable ? (
                    <Popconfirm
                      title="Make this the live prompt?"
                      description="The HR chat will start using this version immediately."
                      okText="Yes, activate"
                      cancelText="Cancel"
                      okButtonProps={{ className: PRIMARY_BTN }}
                      onConfirm={() => setActive(v.version)}
                    >
                      <Button size="small" loading={activating === v.version} data-testid={`prompt-activate-${promptKey}-${v.version}`}>
                        Set active
                      </Button>
                    </Popconfirm>
                  ) : null}
                </div>
              </div>
              {expanded[v.version] && (
                <div className="px-4 pb-3" data-testid={`prompt-version-body-${promptKey}-${v.version}`}>
                  <pre className="whitespace-pre-wrap break-words rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-3 text-xs font-mono text-slate-700 dark:text-gray-200 max-h-72 overflow-auto">{v.body}</pre>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

const Prompts = ({ agent, promptVersions, onChanged }) => {
  const editable = agent?.status === "editable";
  const groups = useMemo(() => Object.entries(promptVersions || {}), [promptVersions]);

  if (groups.length === 0) {
    return (
      <div className="p-10 text-center text-gray-400 dark:text-gray-500" data-testid="prompts-empty">
        No editable prompts for this agent yet.
      </div>
    );
  }

  return (
    <div data-testid="prompts-panel" className="max-w-3xl">
      {!editable && (
        <div className="mb-3 text-sm text-gray-400 dark:text-gray-500">
          This agent is managed in a later release. Prompts are read-only.
        </div>
      )}
      {groups.map(([promptKey, versions]) => (
        <PromptGroup
          key={promptKey}
          agentKey={agent.agent_key}
          promptKey={promptKey}
          versions={versions}
          editable={editable}
          onChanged={onChanged}
        />
      ))}
    </div>
  );
};

export default Prompts;
